import { Container, Title } from "./styles";
import React, { InputHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";

import ErrorDescription from "../../../../ErrorDescription";
import { IconBaseProps } from "react-icons";
import { useField } from "@unform/core";

interface InputNumberProps extends InputHTMLAttributes<HTMLInputElement> {
    name: string;
    icon?: React.ComponentType<IconBaseProps>;
    description?: string;
    max_length?: number;
    is_just_view?: string;
    variation?: string;
}

const InputNumber: React.FC<InputNumberProps> = ({ name, description, icon: Icon, disabled, max_length, is_just_view, variation, ...rest }) => {

    const isJustViewActive = is_just_view !== undefined && is_just_view !== null ? true : false;
    const isJustView = is_just_view !== undefined && is_just_view !== null && is_just_view === "S" ? true : false;

    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const { fieldName, defaultValue, error, registerField } = useField(name);

    const maskNumber = (value: string) => {
        if (!value) return '';

        // Handle percentage formatting when variation is "2"
        if (variation === "2") {
            // Allow just a minus sign
            if (value === '-') return '-';
            
            const isNegative = value.startsWith('-');
            let numericValue = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
            
            // Return empty if no numeric value
            if (numericValue === '') {
                return isNegative ? '-' : '';
            }
            
            return (isNegative ? '-' : '') + numericValue + '%';
        }

        // Allow a negative sign at the beginning of the number
        if (value.startsWith('-')) {
            // Remove any non-digit characters except the leading minus and decimal point
            return '-' + value.substring(1).replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
        }
        // Original behavior for positive numbers
        return value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    };

    const [fieldValue, setFieldValue] = React.useState(() => {
        if (defaultValue !== undefined) {
            // Convert decimal to percentage for display when variation is "2"
            if (variation === "2") {
                const percentValue = Number(defaultValue) * 100;
                return maskNumber(String(percentValue));
            }
            return maskNumber(String(defaultValue));
        }
        return '';
    });

    const handleInputFocused = useCallback(() => {
        setIsFocused(true);
    }, []);

    const handleInputBlur = useCallback(() => {
        setIsFocused(false);
    }, []);

    useEffect(() => {
        registerField({
            name: fieldName,
            ref: inputRef.current,
            getValue: () => {
                if (fieldValue !== undefined && fieldValue !== '') {
                    let value: string = fieldValue.trim();
                    
                    if (value === "" || value === "-") return undefined;
                    
                    // Handle percentage conversion to decimal
                    if (variation === "2") {
                        let numericValue = value.replace(/[^0-9.-]/g, '');
                        
                        if (numericValue === '' || numericValue === '-') return undefined;
                        
                        const percentValue = Number(numericValue);
                        return percentValue / 100;
                    }
                    
                    return Number(value.replace(/[^0-9.-]+/g, ""));
                } else {
                    return undefined;
                }
            },
            setValue: (ref, value) => {
                if (value !== null && value !== undefined) {
                    // Convert decimal to percentage for display when variation is "2"
                    if (variation === "2") {
                        const percentValue = Number(value) * 100;
                        setFieldValue(maskNumber(String(percentValue)));
                    } else {
                        setFieldValue(maskNumber(String(value)));
                    }
                } else {
                    setFieldValue('');
                }
            },
            clearValue: () => {
                setFieldValue('');
            }
        });

    }, [fieldName, registerField, fieldValue, variation]);

    return (
        <>
            {description && <Title>{description}</Title>}
            <Container isErrored={!!error} isFocused={isFocused} isDisabled={isJustViewActive ? isJustView : !!disabled} >
                {Icon && <Icon size="20" />}
                <input
                    onFocus={handleInputFocused}
                    onBlur={handleInputBlur}
                    onChange={(e) => {
                        const inputValue = e.target.value;
                        
                        // Handle backspace to empty or just minus sign
                        if (inputValue === '' || inputValue === '-') {
                            setFieldValue(inputValue);
                        } else {
                            setFieldValue(maskNumber(inputValue));
                        }
                    }}
                    ref={inputRef}
                    disabled={isJustViewActive ? isJustView : !!disabled}
                    maxLength={max_length}
                    value={fieldValue}
                    {...rest}
                />

            </Container>

            {error && (<ErrorDescription title={error} />)}
        </>
    );
}

export default InputNumber;